/**
 * DialTrack.jsx — Length and Formality as a hairline with four stops
 * (design 9b), the same control the extension draws in the studio window
 * (frontend/extension/content-script.js, pa-sw-d*). One stop per engine
 * level, in UI order left → right; only the active level's word prints, so
 * the control is the same width whatever it says.
 *
 * Marker grammar (shape carries the state, never type treatment):
 *   saved  — ink dot inside the dashed ring ("the voice as saved")
 *   user   — orange dot; the dashed ring stays on the saved stop
 *   moment — orange ring (a rule set it; it never fills)
 *   ruled-out stops — hollow, on a dashed stretch of track
 */

// `v` is the value sent for a stop and must resolve to the engine bracket at
// its index — the extension (SW_DIALS) and backend/engine/dials.py
// (STOP_VALUES) keep the same table. `edges` are the engine's bracket
// boundaries; a value on an edge belongs to the higher stop.
export const LEVELS = {
  len: {
    axis: 'economy', label: 'LENGTH', name: 'Length', edges: [0.4, 0.8, 0.95],
    stops: [
      { w: 'Long', v: 0.2, copy: 'Room to explain. The reader gets your reasoning, not just your answer.' },
      { w: 'Medium', v: 0.5, copy: 'The everyday email. Says what it needs to, then stops.' },
      { w: 'Short', v: 0.8, copy: 'A few quick sentences. The point lands before they scroll.' },
      { w: 'TL;DR', v: 0.95, copy: 'The answer and nothing else — reads like a text.' },
    ],
  },
  form: {
    axis: 'unpolished', label: 'FORMALITY', name: 'Formality', edges: [0.15, 0.4, 0.7],
    stops: [
      { w: 'Polished', v: 0, copy: 'Every courtesy kept. Safe for someone you have never met.' },
      { w: 'Natural', v: 0.2, copy: 'Clean, but it sounds like talking. Most work email lives here.' },
      { w: 'Relaxed', v: 0.45, copy: 'Contractions and looser sentences. Someone you already know.' },
      { w: 'Shorthand', v: 0.85, copy: 'How a veteran writes to a peer. Clipped, assumes context.' },
    ],
  },
};

export const TLDR_NOTE = 'Drops the warm close and the detail.';

/** The stop an engine value lands on (bracket edges, boundary → higher stop). */
export function stopOf(key, value) {
  const x = Number(value);
  if (value == null || !Number.isFinite(x)) return null;
  const e = LEVELS[key].edges;
  let i = 0;
  while (i < e.length && x >= e[i]) i += 1;
  return i;
}

/** The stop for a word ("Neutral" was Formality's old name for Natural). */
export function stopOfWord(key, word) {
  const w = word === 'Neutral' ? 'Natural' : word;
  const i = LEVELS[key].stops.findIndex((s) => s.w === w);
  return i < 0 ? null : i;
}

const ALL = [true, true, true, true];

/**
 * One control. `place`: 'rail' (track 120px, label inline — under a draft)
 * or 'tune' (track 90px, label above — beside the hexagon). Interactive only
 * when `onPick` is given: each stop is a 20×20 target and the track is a
 * keyboard slider (←/→ step, skipping ruled-out stops · Home/End · Esc back
 * to the saved stop).
 */
export function DialRow({ dial, value, saved = value, state, allowed = ALL, tag = '', place = 'rail', onPick }) {
  const L = LEVELS[dial];
  const w = place === 'tune' ? 90 : 120;
  const gap = w / 3;
  const x = (i) => 6 + i * gap;
  const st = state || (value === saved ? 'saved' : 'user');
  const step = (dir) => {
    for (let i = value + dir; i >= 0 && i < 4; i += dir) if (allowed[i]) return i;
    return value;
  };
  const onKey = (e) => {
    if (!onPick || e.altKey || e.metaKey || e.ctrlKey) return;
    let to = null;
    if (e.key === 'ArrowLeft') to = step(-1);
    else if (e.key === 'ArrowRight') to = step(1);
    else if (e.key === 'Home') to = allowed.indexOf(true);
    else if (e.key === 'End') to = allowed.lastIndexOf(true);
    else if (e.key === 'Escape' && value !== saved) to = saved;
    else return;
    e.preventDefault();
    if (to !== value) onPick(to);
  };
  const stops = L.stops.map((s, i) => {
    const cl = !allowed[i];
    const marks = (
      <>
        {i === saved && <i className="w-dm w-dm-ring" />}
        {i === value ? <i className={`w-dm w-dm-on w-dm-${st}`} /> : <i className={cl ? 'w-dm w-dm-hol' : 'w-dm w-dm-dot'} />}
      </>
    );
    if (!onPick) return <span key={s.w} className="w-dstop" style={{ left: x(i) - 10 }}>{marks}</span>;
    return (
      <button key={s.w} type="button" className="w-dstop" tabIndex={-1} aria-hidden="true" style={{ left: x(i) - 10 }}
        data-cl={cl ? '' : undefined} onMouseDown={(e) => e.preventDefault()}
        onClick={() => { if (!cl && i !== value) onPick(i); }}>
        {marks}
        <span className="w-dtip" role="tooltip"><span className="w-dtip-w">{s.w}</span>{s.copy}{dial === 'len' && i === 3 && <span className="w-dtip-se">{TLDR_NOTE}</span>}</span>
      </button>
    );
  });
  const track = (
    <span className={`w-dtrack${onPick ? ' w-dtrack-live' : ''}`} style={{ width: w + 12 }}
      {...(onPick ? { role: 'slider', tabIndex: 0, 'aria-label': L.name, 'aria-valuemin': 0, 'aria-valuemax': 3, 'aria-valuenow': value, 'aria-valuetext': L.stops[value].w, onKeyDown: onKey } : { 'aria-hidden': true })}>
      {[0, 1, 2].map((i) => <i key={i} className={`w-dseg${!allowed[i] || !allowed[i + 1] ? ' w-dseg-cl' : ''}`} style={{ left: x(i), width: gap }} />)}
      {stops}
    </span>
  );
  const read = (
    <>
      <span className={`w-dword${st === 'moment' ? ' w-dword-mo' : ''}`}>{L.stops[value].w}</span>
      {tag && <span className="w-dtag">{tag}</span>}
    </>
  );
  if (place === 'tune') {
    return <div><div className="w-dlabel w-dlabel-top">{L.label}</div><div className="w-drow9 w-drow9-wrap">{track}{read}</div></div>;
  }
  return <div className="w-drow9"><span className="w-dlabel">{L.label}</span>{track}{read}</div>;
}

/** The two rows under a draft, plus the TL;DR side-effect line. */
export function DialRows({ length, formality, savedLength = length, savedFormality = formality, onPick, style }) {
  return (
    <div className="w-dials9" style={style}>
      <DialRow dial="len" value={length} saved={savedLength} onPick={onPick ? (i) => onPick('len', i) : undefined} />
      <DialRow dial="form" value={formality} saved={savedFormality} onPick={onPick ? (i) => onPick('form', i) : undefined} />
      {length === 3 && <div className="w-dnote">{TLDR_NOTE}</div>}
    </div>
  );
}
